import { getPlayerRef } from '@/inertia/Shared/MainContainer/Player/Player';
import { useRef } from 'react';
import { Analytics, type EventAction, type EventParams, type ProgressType } from '../analytics';

type CheckSrcType = 'pdf' | 'youtube' | 'vimeo' | 'spotify' | 'soundcloud' | 'mixcloud' | 'unknown';

export const useMediaUtils = (eventName: string) => {
  const isScriptLoaded = useRef(false);
  const percentages = useRef<string[]>([]);
  const isFirstPlay = useRef(false);
  const playbackDuration = useRef(0);

  return {
    isFirstPlay,
    playbackDuration,
    push: (action: EventAction, data?: Partial<EventParams> & Record<string, unknown>): void => {
      Analytics.push('zngly_media_interaction', {
        eventaction: action,
        eventlabel: eventName,
        ...(playbackDuration.current > 0 && { duration: playbackDuration.current }),
        ...data,
      });
    },
    millisecondsToSeconds: (milliseconds: number) => {
      return Math.round(milliseconds / 1000);
    },
    getIframe: () => {
      return getPlayerRef().current?.querySelector('iframe') as HTMLIFrameElement | null;
    },
    getPlayer: () => {
      return getPlayerRef().current as HTMLDivElement | null;
    },
    isScriptLoaded,
    loadScript: (src: string, onLoad?: () => void) => {
      if (isScriptLoaded.current) return;

      // check if script exists in the dom
      const scripts = document.getElementsByTagName('script');
      for (let i = 0; i < scripts.length; i++) {
        if (scripts[i].src === src) {
          isScriptLoaded.current = true;
          onLoad?.();
          return;
        }
      }

      const script = document.createElement('script');
      script.src = src;
      script.defer = true;
      script.type = 'text/javascript';
      script.setAttribute('nonce', window.znRuntime?.cspStrictValue || '');
      script.onload = () => {
        onLoad?.();
        isScriptLoaded.current = true;
      };

      // insert script before the first occurence of the script tag
      const firstScript = document.getElementsByTagName('script')[0];
      firstScript.parentNode?.insertBefore(script, firstScript);
    },
    // calaculates if there is a new percentage to push
    getPercentage: (position: number, duration: number, split = 10): ProgressType | null => {
      // update the duration to be duration minus one percent of the duration
      // this accounts for any rounding errors
      const updatedDuration = (duration / 100) * 99;

      const percentage = (Math.floor((position / updatedDuration) * split) / split).toFixed(2);

      // if we have this percentage already then return null
      if (percentages.current.includes(percentage)) return null;

      // add the percentage to the list of percentages we have already tracked
      percentages.current.push(percentage);

      // we do not want to track 0.00 updates
      if (percentage === '0.00') return null;

      return `progress${Number(percentage) * 100}%` as ProgressType;
    },
    checkSrcType: (src: string): CheckSrcType => {
      const url = new URL(src);

      // general supported iframe checks
      if (url.hostname.includes('vimeo')) return 'vimeo';
      if (url.hostname.includes('spotify')) return 'spotify';
      if (url.hostname.includes('soundcloud')) return 'soundcloud';
      if (url.hostname.includes('mixcloud')) return 'mixcloud';

      // youtube specific checks
      if (url.hostname.includes('youtube')) return 'youtube';
      if (url.hostname.includes('youtu.be')) return 'youtube';
      if (url.hostname.includes('youtube-nocookie.com')) return 'youtube';

      // check if the src is a pdf
      if (src.includes('.pdf')) {
        const ext = url.pathname.split('.').pop();
        if (ext === 'pdf') {
          return 'pdf';
        }
      }

      return 'unknown';
    },
  };
};
